import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { enterpriseApiClient } from '@api/enterprise/client';
import { useGlobalVariable } from '@api/enterprise/globalVariable';
import { eventTypesKeys } from './constants';

type EventTypeResponse = {
  id: string;
  fullEventName?: string | null;
  menuGroup?: string | null;
  menuOrder?: number | null;
  eventNotes?: string | null;
  isCalibration: boolean;
};

type ODataResponse<T> = {
  '@odata.context'?: string;
  value: T[];
};

export const useCalibrationEventTypes = () => {
  return useQuery<string[], Error>({
    queryKey: eventTypesKeys.calibrationTypes(),
    queryFn: async () => {
      const response = await enterpriseApiClient.get<
        ODataResponse<EventTypeResponse>
      >('/api/odata/event-types', {
        params: {
          $filter: 'IsCalibration eq true',
          $select: 'Id,IsCalibration'
        }
      });

      return response.data.value
        .filter(eventType => eventType.isCalibration)
        .map(eventType => eventType.id);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
};

export const useAllowedEventTypes = () => {
  const {
    data: globalVariableData,
    isLoading,
    error
  } = useGlobalVariable('HIDE_ALLOWED_EVENT_TYPES', true);

  const allowedEvents = React.useMemo(() => {
    if (!globalVariableData?.blobText) {
      return ['CALIBRATION']; // Default fallback
    }
    try {
      return JSON.parse(globalVariableData.blobText) as string[];
    } catch {
      return ['CALIBRATION']; // Fallback if JSON is invalid
    }
  }, [globalVariableData?.blobText]);

  return {
    data: allowedEvents,
    isLoading,
    error
  };
};
